// src/hooks/usePersistedUI.js
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { clampDateString, normalizeDateRangeState } from "../utils/dateUtils";

/**
 * 화면별(혹은 기능별) UI 상태(달력/검색어/정렬 등)를
 * - sessionStorage에 자동 저장/복원
 * - (옵션) URL 쿼리와 동기화
 * 까지 한 번에 처리하는 공용 훅
 *
 * @param {string} key        고유 키(예: "reservations", "operation-status")
 * @param {object} defaults   기본 상태({ dateRange, filters, sortOrder ... })
 * @param {object} options    { urlSync?: boolean, restoreOnMount?: boolean }
 */
export default function usePersistedUI(key, defaults, options = {}) {
  const { urlSync = true, restoreOnMount = true } = options;
  const STORAGE_KEY = `ui:${key}:v1`;
  const navigate = useNavigate();
  const location = useLocation();
  const hasRestoredRef = useRef(false);

  // 1) URL → 객체
  const urlState = useMemo(() => {
    if (!urlSync) return null;
    const params = new URLSearchParams(location.search);
    const from = params.get("from");
    const to = params.get("to");
    const sort = params.get("sort");
    const category = params.get("category");
    const user = params.get("user");
    const start = params.get("start");
    const dest = params.get("dest");
    const car = params.get("car");
    const driver = params.get("driver");

    const base = {};

    // dateRange
    if (from || to) base.dateRange = {
      from: clampDateString(from || defaults?.dateRange?.from || ""),
      to: clampDateString(to || defaults?.dateRange?.to || ""),
    };

    // filters
    const f = {
      사용자: user ?? defaults?.filters?.사용자 ?? "",
      출발지: start ?? defaults?.filters?.출발지 ?? "",
      행선지: dest ?? defaults?.filters?.행선지 ?? "",
      차량: car ?? defaults?.filters?.차량 ?? "",
      기사: driver ?? defaults?.filters?.기사 ?? "",
    };
    // 값이 하나라도 있으면 반영
    if ([user, start, dest, car, driver].some((v) => v !== null)) {
      base.filters = f;
    }

    // sortOrder/category
    if (sort) base.sortOrder = sort;
    if (category) base.category = category;

    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // 2) sessionStorage → 객체
  const sessionState = useMemo(() => {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    try { return normalizeDateRangeState(JSON.parse(raw)); } catch { return null; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [STORAGE_KEY]);

  // 3) 최종 초기값: URL > session > defaults
  const initial = useMemo(() => {
    return normalizeDateRangeState({
      ...(defaults || {}),
      ...(sessionState || {}),
      ...(urlSync ? (urlState || {}) : {}),
    });
  }, [defaults, sessionState, urlState, urlSync]);

  const [state, setState] = useState(initial);

  // 4) 마운트 시 한 번 강제 복원(선택)
  useEffect(() => {
    if (!restoreOnMount || hasRestoredRef.current) return;
    hasRestoredRef.current = true;
    setState(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 5) 상태 → sessionStorage 저장
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state, STORAGE_KEY]);

  // 6) 상태 → URL 동기화(옵션)
  useEffect(() => {
    if (!urlSync) return;
    const params = new URLSearchParams(location.search);

    // dateRange
    if (state?.dateRange?.from) params.set("from", state.dateRange.from);
    else params.delete("from");
    if (state?.dateRange?.to) params.set("to", state.dateRange.to);
    else params.delete("to");

    // filters
    const f = state?.filters || {};
    const map = [
      ["user", f.사용자],
      ["start", f.출발지],
      ["dest", f.행선지],
      ["car", f.차량],
      ["driver", f.기사],
    ];
    map.forEach(([k, v]) => (v ? params.set(k, v) : params.delete(k)));

    // sort/category
    state?.sortOrder ? params.set("sort", state.sortOrder) : params.delete("sort");
    state?.category ? params.set("category", state.category) : params.delete("category");

    const next = `${location.pathname}?${params.toString()}`;
    // 동일하면 교체 안 함
    if (next !== `${location.pathname}${location.search}`) {
      // replace로 히스토리 누적 방지
      navigate(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, urlSync]);

  // 7) 부분 업데이트 헬퍼
  const setPartial = (patch) => {
    setState((prev) => ({ ...prev, ...(typeof patch === "function" ? patch(prev) : patch) }));
  };

  // 8) input 바인딩 헬퍼(필드명만 넘기면 value/onChange 반환)
  const bind = (path) => {
    // path 예: "filters.사용자" | "dateRange.from" | "sortOrder"
    const getter = () => {
      const parts = path.split(".");
      let cur = state;
      for (const p of parts) cur = cur?.[p];
      return cur ?? "";
    };
    const setter = (v) => {
      setState((prev) => {
        const parts = path.split(".");
        const next = structuredClone(prev);
        let cur = next;
        for (let i = 0; i < parts.length - 1; i++) {
          const p = parts[i];
          cur[p] = cur[p] ?? {};
          cur = cur[p];
        }
        cur[parts[parts.length - 1]] = path.startsWith("dateRange.") ? clampDateString(v) : v;
        return next;
      });
    };
    return {
      value: getter(),
      onChange: (e) => setter(e?.target ? e.target.value : e),
    };
  };

  return { state, setState, setPartial, bind };
}
